/**
 * useUploadQueue — Jobsite Capture Arc Stage 2A (2026-06-15).
 *
 * Subscribes to the IDB-backed upload queue, exposes a queue-summary
 * shape any consumer can render, and wires the cross-platform drain
 * triggers:
 *
 *   - browser 'online' event           → drain
 *   - tab visibility → visible         → drain (THIS is iOS Safari's
 *                                        substitute for Background Sync;
 *                                        the queue still drains, just
 *                                        only when the user reopens the
 *                                        tab — captures never get lost)
 *   - 30s page-driven heartbeat        → drain (covers flaky networks
 *                                        that flap-and-recover without
 *                                        firing the 'online' event)
 *   - mount                            → drain
 *
 * On Chromium, the SW Background Sync registration in enqueue() takes
 * over while the tab is hidden. The hook subscribes to SW postMessage
 * events and refreshes the same way as a page-driven drain.
 */
import { useEffect, useMemo, useState } from 'react';
import * as queue from '../lib/uploadQueue';

const DRAIN_HEARTBEAT_MS = 30_000;

export function useUploadQueue() {
  const [items, setItems] = useState([]);
  const [isOnline, setIsOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine !== false,
  );
  const [isHidden, setIsHidden] = useState(
    typeof document === 'undefined' ? false : document.visibilityState === 'hidden',
  );
  const [lastDrainedAt, setLastDrainedAt] = useState(null);

  // Subscribe to queue changes.
  useEffect(() => {
    const unsubscribe = queue.subscribe((evt) => {
      if (evt && evt.items) {
        setItems(evt.items);
      }
      if (evt && evt.swEvent && evt.swEvent.type === 'keymade-upload-drain-complete') {
        setLastDrainedAt(Date.now());
      }
    });
    return unsubscribe;
  }, []);

  // Drain triggers.
  useEffect(() => {
    const drain = () => {
      queue.drainNow().then(() => setLastDrainedAt(Date.now())).catch(() => {});
    };

    const onOnline = () => {
      setIsOnline(true);
      drain();
    };
    const onOffline = () => setIsOnline(false);
    const onVisibility = () => {
      const hidden = document.visibilityState === 'hidden';
      setIsHidden(hidden);
      if (!hidden) drain();
    };

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onVisibility);

    // First drain on mount.
    drain();

    const interval = setInterval(drain, DRAIN_HEARTBEAT_MS);

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(interval);
    };
  }, []);

  const summary = useMemo(() => {
    const pending = items.filter((i) => i.status !== 'uploaded' && (i.attempts || 0) < 5);
    const failed = items.filter((i) => i.status === 'failed');
    const uploading = items.filter((i) => i.status === 'uploading');
    const idle = items.filter((i) => i.status === 'pending');
    const stuck = queue.describeStuckState({ items, isHidden });
    return {
      items, pending, failed, uploading, idle,
      pendingCount: pending.length,
      failedCount: failed.length,
      uploadingCount: uploading.length,
      isOnline, isHidden, lastDrainedAt,
      stuck,
    };
  }, [items, isOnline, isHidden, lastDrainedAt]);

  return {
    ...summary,
    enqueue: queue.enqueue,
    retry: queue.retry,
    remove: queue.remove,
    drainNow: queue.drainNow,
  };
}
